import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Send, Eye, Lock, Undo2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import ReportWorkflowTrail from '../reports/ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function APPaymentsTab({
    suppliers = [],
    fmt,
    onRefresh,
    selectedCourse,
    permissions,
    viewingStudentId,
    viewingStudentName,
    isReadOnly = false,
    sourceOpenRequest = null
}) {
    // Default permissions (student-level) if not provided
    const basePerms = permissions || {
        can_create: true,
        can_edit_draft: true,
        can_post: false,
        can_cancel: false,
        can_unpost: false,
        can_view_reports: true
    };
    const perms = isReadOnly
        ? { ...basePerms, can_create: false, can_edit_draft: false, can_post: false, can_cancel: false, can_unpost: false }
        : basePerms;
    const canPostInCurrentScope = !isReadOnly && perms.can_post;
    const canReverseInCurrentScope = basePerms.can_unpost && (!isReadOnly || !!viewingStudentId);
    const draftReviewer = canPostInCurrentScope
        ? 'You can review and post it after checking supplier, voucher, bank, and support.'
        : 'Submit it for instructor/admin review when ready. Submitted payments appear in Review > AR/AP Review Queue.';
    const draftNextAction = canPostInCurrentScope
        ? 'Open the payment row to review details before posting.'
        : 'Click Submit for Review so the instructor can approve/post it or return it with feedback.';
    
    const [payments, setPayments] = useState([]);
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showReversalDialog, setShowReversalDialog] = useState(false);
    const [reversalTarget, setReversalTarget] = useState(null);
    const [reversalReason, setReversalReason] = useState('');
    const [reversing, setReversing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const lastSourceOpenRequestId = useRef(null);
    const [formData, setFormData] = useState({
        supplier_id: '',
        payment_type: 'direct',
        payment_date: new Date().toISOString().split('T')[0],
        voucher_id: '',
        amount: 0,
        bank_code: '',
        check_number: '',
        particulars: ''
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const paymentQuery = params.toString() ? `?${params.toString()}` : '';
            const voucherParams = new URLSearchParams(params);
            voucherParams.append('status', 'posted');
            const [paymentsRes, vouchersRes] = await Promise.all([
                axios.get(`${API}/ap/payments${paymentQuery}`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/ap/vouchers?${voucherParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setPayments(paymentsRes.data);
            setVouchers(vouchersRes.data.filter(v => v.balance > 0)); // Only show unpaid vouchers
        } catch (error) {
            toast.error('Failed to load AP payments');
        } finally {
            setLoading(false);
        }
    }, [viewingStudentId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!sourceOpenRequest?.id || !sourceOpenRequest.sourceType?.startsWith('ap_payment') || loading) return;
        if (lastSourceOpenRequestId.current === sourceOpenRequest.id) return;

        const requestedId = String(sourceOpenRequest.sourceId || '').trim();
        const requestedRef = String(sourceOpenRequest.refNo || '').trim().toLowerCase();
        const matchingPayment = payments.find((payment) => {
            const paymentId = String(payment.id || '').trim();
            const paymentNumber = String(payment.payment_number || '').trim();
            const paymentNumberLower = paymentNumber.toLowerCase();
            return (requestedId && paymentId === requestedId)
                || (requestedId && paymentNumber === requestedId)
                || (requestedRef && paymentNumberLower === requestedRef)
                || (requestedRef && requestedRef === `rev-${paymentNumberLower}`);
        });

        if (!matchingPayment && payments.length === 0) return;

        lastSourceOpenRequestId.current = sourceOpenRequest.id;
        if (matchingPayment) {
            setSelectedPayment(matchingPayment);
            toast.success(`Opened ${sourceOpenRequest.label || 'AP Payment'} ${matchingPayment.payment_number} from ${sourceOpenRequest.sourceOrigin || 'the source trail'}.`);
        } else {
            toast.info(`The AP Payment source ${sourceOpenRequest.refNo || sourceOpenRequest.sourceId || ''} is not visible in this current course/student scope.`);
        }
    }, [sourceOpenRequest, payments, loading]);

    const selectedVoucher = vouchers.find(v => v.voucher_number === formData.voucher_id);
    const selectedVoucherBalance = parseFloat(selectedVoucher?.balance || 0);

    const handleSubmit = async () => {
        if (!formData.supplier_id || formData.amount <= 0) {
            toast.error('Please fill all required fields');
            return;
        }

        if (formData.payment_type === 'voucher' && !formData.voucher_id) {
            toast.error('Please select a voucher for voucher-based payment');
            return;
        }

        if (formData.payment_type === 'voucher' && selectedVoucher && formData.amount - selectedVoucherBalance > 0.01) {
            toast.error(`Payment cannot exceed voucher balance ${fmt(selectedVoucherBalance)}`);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/ap/payments`, {
                ...formData,
                course_id: selectedCourse || null,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const supplier = suppliers.find(s => s.supplier_id === formData.supplier_id || s.id === formData.supplier_id);
            const paymentNumber = response.data?.payment_number || 'New payment';
            setLastResult({
                title: `AP Payment ${paymentNumber} saved`,
                status: 'Draft',
                amount: formData.amount,
                source: supplier?.supplier_name || selectedVoucher?.supplier_name || formData.supplier_id,
                reference: formData.payment_type === 'voucher' ? (formData.voucher_id || 'Voucher payment') : (formData.check_number || formData.bank_code || 'Direct payment'),
                location: 'Accounts Payable > Payments list. Submit it next to send it to Review > AR/AP Review Queue.',
                reviewer: draftReviewer,
                glImpact: 'Draft only. It does not affect GL, Trial Balance, or reports until posted.',
                nextAction: draftNextAction
            });
            toast.success(
                `AP Payment ${response.data?.payment_number || ''} saved as Draft. Find it in Accounts Payable > Payments; it is not in GL/Trial Balance yet. Next: click Submit for Review so the instructor can approve/post or return it.`
            );
            setShowForm(false);
            resetForm();
            fetchData();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create payment');
        }
    };

    const handleSubmitForReview = async (payment) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/ap/payments/${payment.payment_number}/submit`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updated = response.data?.payment || payment;
            setLastResult({
                title: `AP Payment ${updated.payment_number || payment.payment_number} submitted`,
                status: 'Submitted for Review',
                amount: updated.amount || payment.amount,
                source: updated.supplier_name || payment.supplier_name || 'Supplier payment',
                reference: updated.voucher_number || updated.check_number || updated.bank_code || 'AP payment review',
                location: response.data?.next_location || 'Review > AR/AP Review Queue',
                reviewer: 'Instructor/admin reviews next and can approve/post or return with feedback.',
                glImpact: response.data?.gl_impact || 'Submitted only. No GL or Trial Balance impact until posted.',
                nextAction: 'Wait for instructor review, or monitor this payment status in Accounts Payable > Payments.'
            });
            toast.success(`AP Payment ${updated.payment_number || payment.payment_number} submitted for instructor/admin review. Find it now in Review > AR/AP Review Queue and in your AP Payments list as Submitted for Review; GL/Trial Balance changes only after posting.`);
            fetchData();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit payment for review');
        }
    };

    const handlePost = async (paymentNumber) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/ap/payments/${paymentNumber}/post`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const target = payments.find(p => p.payment_number === paymentNumber);
            setLastResult({
                title: `AP Payment ${paymentNumber} posted`,
                status: 'Posted',
                amount: target?.amount,
                source: target?.supplier_name || 'Supplier payment',
                reference: target?.voucher_number || target?.check_number || target?.bank_code || 'Payment posting',
                location: 'Accounts Payable > Payments, General Ledger, Trial Balance, and AP reports',
                reviewer: 'Instructor/admin has posted it; auditors can review posted evidence.',
                glImpact: 'Posted payment now affects GL, Trial Balance, AP balances, and related reports.',
                nextAction: 'Verify the GL lines and AP balance. Use reversal only if the posted payment needs correction.'
            });
            toast.success(
                `AP Payment ${paymentNumber} posted. It now affects GL and Trial Balance; instructors can find it in Dashboard > Recent Student Accounting Activity and Review > Payments.`
            );
            fetchData();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to post payment');
        }
    };

    const openReversalDialog = (payment) => {
        setReversalTarget(payment);
        setReversalReason('');
        setShowReversalDialog(true);
    };

    const handleReverse = async () => {
        if (!reversalReason.trim()) {
            toast.error('Please provide a reason for reversal');
            return;
        }
        
        setReversing(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const response = await axios.put(
                `${API}/ap/payments/${reversalTarget.payment_number}/reverse${params.toString() ? `?${params.toString()}` : ''}`,
                { reason: reversalReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLastResult({
                title: `AP Payment ${reversalTarget.payment_number} reversed`,
                status: 'Reversed',
                amount: reversalTarget.amount,
                source: reversalTarget.supplier_name || 'Supplier payment',
                reference: response.data.reversal_ref || reversalTarget.reversal_ref || 'Reversal entry',
                location: 'Accounts Payable > Payments, General Ledger, and reversal evidence',
                reviewer: 'Instructor/admin completed the reversal; auditors can review the reason and reference.',
                glImpact: 'Reversal creates offsetting GL entries and restores related voucher balance when applicable.',
                nextAction: 'Review the reversal reference, then create a corrected payment only if the original payment was wrong.'
            });
            toast.success(
                `AP Payment ${reversalTarget.payment_number} reversed. Reference: ${response.data.reversal_ref}. Review it in Accounts Payable > Payments and General Ledger.`
            );
            setShowReversalDialog(false);
            setReversalTarget(null);
            setReversalReason('');
            fetchData();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reverse payment');
        } finally {
            setReversing(false);
        }
    };

    const resetForm = () => {
        setFormData({
            supplier_id: '',
            payment_type: 'direct',
            payment_date: new Date().toISOString().split('T')[0],
            voucher_id: '',
            amount: 0,
            bank_code: '',
            check_number: '',
            particulars: ''
        });
    };

    const handleSupplierChange = (supplierId) => {
        setFormData(prev => ({ ...prev, supplier_id: supplierId, voucher_id: '' }));
    };

    const handleVoucherSelect = (voucherId) => {
        const voucher = vouchers.find(v => v.voucher_number === voucherId);
        if (voucher) {
            setFormData(prev => ({
                ...prev,
                voucher_id: voucherId,
                amount: voucher.balance, // Default to remaining balance
                supplier_id: voucher.supplier_id
            }));
        }
    };

    const getDisplayStatus = (record) => {
        if (record.review_status === 'returned') return 'returned';
        if (record.status === 'submitted') return 'submitted';
        return record.status || 'draft';
    };

    const getStatusBadge = (record) => {
        const status = getDisplayStatus(record);
        const styles = {
            draft: 'bg-amber-100 text-amber-700',
            submitted: 'bg-blue-100 text-blue-700',
            returned: 'bg-orange-100 text-orange-700',
            posted: 'bg-emerald-100 text-emerald-700',
            reversed: 'bg-purple-100 text-purple-700'
        };
        const labels = {
            draft: 'Draft',
            submitted: 'Submitted for Review',
            returned: 'Returned for Revision',
            posted: 'Posted',
            reversed: 'Reversed'
        };
        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.draft}`}>{labels[status] || status}</span>;
    };

    const filteredVouchers = formData.supplier_id 
        ? vouchers.filter(v => v.supplier_id === formData.supplier_id)
        : vouchers;

    const workflowScope = viewingStudentName
        ? `Reviewing ${viewingStudentName}`
        : isReadOnly
            ? 'Read-only review'
            : 'Your workspace';

    const workflowItems = [
        {
            label: 'Source work',
            value: 'AP payments start as draft supplier payments or payments linked to posted AP vouchers.'
        },
        {
            label: 'Where it goes',
            value: 'Saved payments stay in Accounts Payable > Payments and appear in instructor activity for review.'
        },
        {
            label: 'Approval',
            value: canPostInCurrentScope
                ? 'Instructors/admins post or reverse after checking supplier and voucher support.'
                : 'Students submit AP payments for instructor/admin review. Submitted work appears in Review > AR/AP Review Queue.'
        },
        {
            label: 'GL and TB',
            value: 'Only posted payments and reversal entries affect GL, Trial Balance, AP balances, and reports.'
        },
        {
            label: 'Next safe action',
            value: canPostInCurrentScope
                ? 'Review draft details before posting. For posted mistakes, use reversal and keep the audit reason.'
                : 'Save the payment, then submit it for review. It affects GL/Trial Balance only after instructor posting.'
        }
    ];

    const getPaymentDetailTrail = (payment) => {
        const status = getDisplayStatus(payment);
        const reversalRef = payment.reversal_ref || payment.reversal_reference || payment.reversal_number;
        const voucherRef = payment.voucher_number ? ` Linked AP Voucher: ${payment.voucher_number}.` : '';

        if (status === 'posted') {
            return {
                location: `Accounts Payable > Payments, General Ledger, Trial Balance, AP reports, and AP voucher evidence when linked.${voucherRef}`,
                reviewer: 'No reviewer is waiting. Instructor/admin already posted this payment; auditors can review it read-only.',
                impact: 'Affects GL, Trial Balance, AP balances, and related reports.',
                nextAction: 'Verify the GL lines and AP/vendor balance. Use reversal only if the posted payment needs correction.'
            };
        }
        if (status === 'reversed') {
            return {
                location: `Accounts Payable > Payments and General Ledger${reversalRef ? ` via reversal reference ${reversalRef}` : ''}.${voucherRef}`,
                reviewer: 'No reviewer is waiting. The reversal is audit evidence for the original posted payment.',
                impact: 'Reversal entries offset the original AP payment impact in GL, Trial Balance, AP balances, and reports. Voucher balance is restored when the payment was voucher-linked.',
                nextAction: 'Review the reversal reason and reference. Create a corrected payment only if the supplier payment still needs posting.'
            };
        }
        if (status === 'submitted') {
            return {
                location: 'Review > AR/AP Review Queue and Accounts Payable > Payments list.',
                reviewer: 'Instructor/admin reviews next and can post it or return it with feedback.',
                impact: 'Submitted only. It does not affect GL, Trial Balance, AP balances, or reports until posted.',
                nextAction: 'Wait for review or monitor this payment status in Accounts Payable > Payments.'
            };
        }
        if (status === 'returned') {
            return {
                location: 'Accounts Payable > Payments list as Returned for Revision.',
                reviewer: 'Student revises next, then resubmits for instructor/admin review.',
                impact: 'Returned work is evidence only. It does not affect GL, Trial Balance, AP balances, or reports.',
                nextAction: 'Read the feedback, revise supplier/voucher/bank/payment details, and resubmit only when corrected.'
            };
        }
        return {
            location: 'Accounts Payable > Payments list.',
            reviewer: draftReviewer,
            impact: 'Draft only. It does not affect GL, Trial Balance, AP balances, or reports until posted.',
            nextAction: draftNextAction
        };
    };

    return (
        <div className="space-y-6" data-testid="ap-payments-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">AP Payments</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Review supplier payments for ${viewingStudentName}` : 'Manage payments to suppliers'}
                    </p>
                </div>
                {!isReadOnly && (
                    <Button onClick={() => setShowForm(true)} className="bg-[#2F8FCB]" data-testid="new-payment-btn">
                        <Plus className="w-4 h-4 mr-2" /> New Payment
                    </Button>
                )}
            </div>

            {isReadOnly && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ap-payments-readonly-scope">
                    Student review mode is read-only. Use this list to verify payments the student created or posted, then check General Ledger or Trial Balance for posted accounting effect.
                </div>
            )}

            <ReportWorkflowTrail
                title="AP Payments trail"
                scope={workflowScope}
                items={workflowItems}
                testId="ap-payments-workflow-trail"
            />

            {lastResult && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950" data-testid="ap-payment-result-trail">
                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="font-semibold">{lastResult.title}</p>
                            <p className="mt-1 text-emerald-800">
                                Status: <span className="font-semibold">{lastResult.status}</span>
                                {lastResult.amount !== undefined && lastResult.amount !== null ? ` | Amount: ${fmt(lastResult.amount)}` : ''}
                            </p>
                        </div>
                        <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                            Latest action
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <p><span className="font-semibold">Created/changed:</span> {lastResult.source}</p>
                        <p><span className="font-semibold">Reference:</span> {lastResult.reference}</p>
                        <p><span className="font-semibold">Find it now:</span> {lastResult.location}</p>
                        <p><span className="font-semibold">Review:</span> {lastResult.reviewer}</p>
                        <p><span className="font-semibold">Accounting impact:</span> {lastResult.glImpact}</p>
                        <p><span className="font-semibold">Next safe action:</span> {lastResult.nextAction}</p>
                    </div>
                </div>
            )}

            {/* Payment List */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                            <th className="text-left px-4 py-3">Payment #</th>
                            <th className="text-left px-4 py-3">Supplier</th>
                            <th className="text-left px-4 py-3">Type</th>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-right px-4 py-3">Amount</th>
                            <th className="text-center px-4 py-3">Status</th>
                            <th className="text-center px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
                        ) : payments.length === 0 ? (
                            <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No AP payments found</td></tr>
                        ) : (
                            payments.map((p) => (
                                <tr key={p.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-mono text-sm">{p.payment_number}</td>
                                    <td className="px-4 py-3">
                                        <p className="font-medium">{p.supplier_name}</p>
                                        <p className="text-xs text-slate-400">{p.supplier_id}</p>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                            p.payment_type === 'voucher' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                            {p.payment_type}
                                        </span>
                                        {p.voucher_number && <p className="text-xs text-slate-400 mt-0.5">{p.voucher_number}</p>}
                                    </td>
                                    <td className="px-4 py-3">{p.payment_date}</td>
                                    <td className="px-4 py-3 text-right font-mono">{fmt(p.amount)}</td>
                                    <td className="px-4 py-3 text-center">
                                        {getStatusBadge(p)}
                                        {p.review_status === 'returned' && (
                                            <div className="mx-auto mt-2 max-w-[240px] rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-left text-xs text-orange-800" data-testid={`returned-ap-payment-row-note-${p.payment_number}`}>
                                                <p className="font-semibold">Returned: revise this payment, then resubmit.</p>
                                                <p className="mt-0.5 line-clamp-2">Feedback: {p.return_reason || 'Open the payment details to read instructor feedback.'}</p>
                                                <p className="mt-0.5">No GL, Trial Balance, or AP report impact until posted.</p>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <TooltipProvider>
                                        <div className="flex items-center justify-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setSelectedPayment(p)}
                                                data-testid={`view-payment-${p.payment_number}`}
                                                aria-label={`View payment ${p.payment_number} source details`}
                                                title={`View payment ${p.payment_number} source details`}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            {p.status === 'draft' && !isReadOnly && (
                                                perms.can_post ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-emerald-600"
                                                        onClick={() => handlePost(p.payment_number)}
                                                        data-testid={`post-payment-${p.payment_number}`}
                                                    >
                                                        <Send className="w-4 h-4" />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-blue-600"
                                                        onClick={() => handleSubmitForReview(p)}
                                                        data-testid={`submit-payment-${p.payment_number}`}
                                                    >
                                                        <Send className="w-4 h-4" />
                                                    </Button>
                                                )
                                            )}
                                            {p.status === 'submitted' && !isReadOnly && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="sm" className="text-blue-500 cursor-not-allowed" disabled>
                                                            <Lock className="w-4 h-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Submitted to Review &gt; AR/AP Review Queue</p></TooltipContent>
                                                </Tooltip>
                                            )}
                                            {p.status === 'posted' && (
                                                canReverseInCurrentScope ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-purple-600"
                                                                onClick={() => openReversalDialog(p)}
                                                                data-testid={`reverse-payment-${p.payment_number}`}
                                                            >
                                                                <Undo2 className="w-4 h-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Reverse (Unpost) this payment</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button variant="ghost" size="sm" className="text-slate-300 cursor-not-allowed" disabled>
                                                                <Lock className="w-4 h-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Only instructors can reverse payments</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )
                                            )}
                                            {p.status === 'reversed' && p.reversal_ref && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="text-xs text-purple-600 font-mono">{p.reversal_ref}</span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Reversed by: {p.reversed_by_name || 'N/A'}</p>
                                                        <p>Reason: {p.reversal_reason || 'N/A'}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                        </TooltipProvider>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Reversal Confirmation Dialog */}
            <Dialog open={showReversalDialog} onOpenChange={setShowReversalDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-purple-700">Reverse AP Payment</DialogTitle>
                        <DialogDescription className="text-sm text-slate-600">
                            Create reversing GL entries and restore the related AP voucher balance when applicable. This action requires a reason and cannot be undone directly.
                        </DialogDescription>
                    </DialogHeader>
                    {reversalTarget && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-4">
                            <p className="text-sm"><strong>Payment:</strong> {reversalTarget.payment_number}</p>
                            <p className="text-sm"><strong>Supplier:</strong> {reversalTarget.supplier_name}</p>
                            <p className="text-sm"><strong>Amount:</strong> {fmt ? fmt(reversalTarget.amount) : reversalTarget.amount}</p>
                            {reversalTarget.voucher_number && (
                                <p className="text-sm"><strong>Voucher:</strong> {reversalTarget.voucher_number}</p>
                            )}
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="payment-reversal-reason">Reason for Reversal *</Label>
                        <Textarea
                            id="payment-reversal-reason"
                            placeholder="e.g., Payment sent to wrong supplier, duplicate payment, etc."
                            value={reversalReason}
                            onChange={(e) => setReversalReason(e.target.value)}
                            rows={3}
                            data-testid="payment-reversal-reason-input"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowReversalDialog(false)}>Cancel</Button>
                        <Button 
                            onClick={handleReverse} 
                            disabled={reversing || !reversalReason.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="confirm-payment-reversal-btn"
                        >
                            {reversing ? 'Reversing...' : 'Confirm Reversal'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* RBAC Notice for Students */}
            {!isReadOnly && !perms.can_post && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                    <Lock className="w-5 h-5 text-amber-600" />
                    <div>
                        <p className="text-sm font-medium text-amber-800">Student Mode</p>
                        <p className="text-xs text-amber-600">You can create draft payments. Only instructors can post payments.</p>
                    </div>
                </div>
            )}

            {/* New Payment Form Dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>New AP Payment</DialogTitle>
                        <DialogDescription>
                            Create a draft supplier payment. Draft AP payments stay in Accounts Payable &gt; Payments and do not affect GL, Trial Balance, AP balances, or reports until posted.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Payment Type *</Label>
                            <Select value={formData.payment_type} onValueChange={(v) => setFormData(prev => ({ ...prev, payment_type: v }))}>
                                <SelectTrigger data-testid="payment-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="direct">Direct Payment</SelectItem>
                                    <SelectItem value="voucher">Voucher Payment</SelectItem>
                                    <SelectItem value="advance">Advance Payment</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {formData.payment_type === 'voucher' ? (
                            <div>
                                <Label>Select Voucher *</Label>
                                <Select value={formData.voucher_id} onValueChange={handleVoucherSelect}>
                                    <SelectTrigger data-testid="voucher-select">
                                        <SelectValue placeholder="Select unpaid voucher" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {vouchers.map(v => (
                                            <SelectItem key={v.voucher_number} value={v.voucher_number}>
                                                {v.voucher_number} - {v.supplier_name} - Balance: {fmt(v.balance)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div>
                                <Label>Supplier *</Label>
                                <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                                    <SelectTrigger data-testid="supplier-select">
                                        <SelectValue placeholder="Select supplier" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {suppliers.map(s => (
                                            <SelectItem key={s.supplier_id} value={s.supplier_id}>
                                                {s.supplier_id} - {s.supplier_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Payment Date *</Label>
                                <Input
                                    type="date"
                                    value={formData.payment_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label>Amount *</Label>
                                <Input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                                    max={formData.payment_type === 'voucher' && selectedVoucher ? selectedVoucherBalance : undefined}
                                    data-testid="payment-amount-input"
                                />
                                {formData.payment_type === 'voucher' && selectedVoucher && (
                                    <p className="mt-1 text-xs text-slate-500">
                                        Remaining voucher balance: {fmt(selectedVoucherBalance)}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Bank/Mode</Label>
                                <Input
                                    value={formData.bank_code}
                                    onChange={(e) => setFormData(prev => ({ ...prev, bank_code: e.target.value }))}
                                    placeholder="e.g., BPI, Cash"
                                />
                            </div>
                            <div>
                                <Label>Check #</Label>
                                <Input
                                    value={formData.check_number}
                                    onChange={(e) => setFormData(prev => ({ ...prev, check_number: e.target.value }))}
                                    placeholder="Check number"
                                />
                            </div>
                        </div>

                        <div>
                            <Label>Particulars</Label>
                            <Textarea
                                value={formData.particulars}
                                onChange={(e) => setFormData(prev => ({ ...prev, particulars: e.target.value }))}
                                placeholder="Payment notes"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
                        <Button onClick={handleSubmit} className="bg-[#2F8FCB]" data-testid="save-payment-btn">Save Payment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Payment Dialog */}
            <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>Payment Details</DialogTitle>
                        <DialogDescription>
                            Review AP payment source evidence, status, linked voucher, GL/Trial Balance impact, and next safe action.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedPayment && (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div><span className="text-slate-500">Payment #:</span> <strong>{selectedPayment.payment_number}</strong></div>
                                <div><span className="text-slate-500">Status:</span> {getStatusBadge(selectedPayment)}</div>
                                <div><span className="text-slate-500">Supplier:</span> {selectedPayment.supplier_name}</div>
                                <div><span className="text-slate-500">Type:</span> {selectedPayment.payment_type}</div>
                                <div><span className="text-slate-500">Date:</span> {selectedPayment.payment_date}</div>
                                <div><span className="text-slate-500">Amount:</span> <strong className="text-emerald-600">{fmt(selectedPayment.amount)}</strong></div>
                                {selectedPayment.voucher_number && (
                                    <div><span className="text-slate-500">Voucher:</span> {selectedPayment.voucher_number}</div>
                                )}
                                {selectedPayment.bank_code && (
                                    <div><span className="text-slate-500">Bank:</span> {selectedPayment.bank_code}</div>
                                )}
                                {selectedPayment.check_number && (
                                    <div><span className="text-slate-500">Check #:</span> {selectedPayment.check_number}</div>
                                )}
                            </div>
                            {selectedPayment.particulars && (
                                <div>
                                    <span className="text-slate-500">Particulars:</span>
                                    <p>{selectedPayment.particulars}</p>
                                </div>
                            )}
                            {selectedPayment.review_status === 'returned' && (
                                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-orange-900">
                                    <p className="font-semibold">Returned for revision</p>
                                    <p className="mt-1">{selectedPayment.return_reason || 'Please revise this payment and resubmit for review.'}</p>
                                    {selectedPayment.returned_at && (
                                        <p className="mt-1 text-xs text-orange-700">Returned at {new Date(selectedPayment.returned_at).toLocaleString()}</p>
                                    )}
                                </div>
                            )}
                            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm" data-testid="ap-payment-detail-trail">
                                <p className="font-semibold text-blue-900">AP Payment evidence trail</p>
                                <p className="mt-1 text-blue-800">
                                    This detail view is read-only evidence. It does not post, reverse, edit, delete, or change GL/Trial Balance records.
                                </p>
                                <div className="mt-3 grid gap-2 md:grid-cols-2">
                                    {(() => {
                                        const trail = getPaymentDetailTrail(selectedPayment);
                                        return [
                                            ['Where to find it', trail.location],
                                            ['Who reviews next', trail.reviewer],
                                            ['GL / Trial Balance', trail.impact],
                                            ['Next safe action', trail.nextAction],
                                        ].map(([label, value]) => (
                                            <div key={label} className="rounded-md border border-blue-100 bg-white/80 p-2">
                                                <span className="block text-[11px] font-semibold uppercase text-blue-700">{label}</span>
                                                <span className="mt-1 block text-xs text-slate-700">{value}</span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedPayment(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
